/**
 * The /rate email ask: the trends signup, tagged source "rate".
 *
 * Same endpoint and same format-context fields as the post-mock ask in
 * ResultsScreen (api/email/trends-signup.js), except the context here is the
 * REAL league that was just graded, read off the report. The optional date is
 * for the user's next draft, since the one on this page already happened.
 *
 * 🔴 TWO PLACES ASK, ONE COMPONENT DOES IT (Aug 30 2026). The team report's
 * gate panel and the league report's ask at the bottom of the page are the same
 * form with different words and a different `source` tag, so there is exactly
 * one submit path, one validation, one endpoint and one set of format fields. A
 * second copy of this is how one of the two would quietly stop sending qbSlots.
 * Round 5 took the team report down to ONE ask, the gate, so those two are now
 * on different pages rather than one above the other.
 *
 * `onSubmitted` is what makes the gate a gate: the caller unlocks on it. It
 * fires on a SUCCESSFUL post and nowhere else - an unlock granted on a failed
 * request would be an unlock the user's address never bought.
 *
 * 🔴 THE DATE FIELD IS OPTIONAL ON THE COMPONENT, NOT JUST IN THE FORM (round
 * 5). The gate is now the first thing between a reader and their own report, so
 * it asks for ONE thing: an address. A second field on that form is a second
 * reason to close the tab, and the draft date buys nothing the report needs.
 * `askDate={false}` drops the input entirely and the post carries draftAt null,
 * which the endpoint already files under its unscheduled bucket. The league
 * report's ask keeps the field, because there the date is the whole point of
 * the offer: trends the day before the next draft.
 */

import { useState } from "react";
import { epochFromLocalDate } from "../../utils/liveBoard.js";
import { C, SZ, card, input, label, primaryBtn, errorText, successText, fineText } from "../Submit/submitStyles.js";

// Must match EMAIL_PATTERN in api/email/trends-signup.js.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EmailAsk({
  report,
  source = "rate",
  title = "Want this as a full report with charts by email?",
  blurb = "One email, built from the same real auction data. Add your next draft date and the market trends land the day before you pick.",
  cta = "Email me the full report",
  doneText = "You're on the list. The full report lands in your inbox.",
  askDate = true,
  onSubmitted,
  testId,
}) {
  const [email, setEmail] = useState("");
  const [date, setDate] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState(null);

  // Both asks can be on the page at once, so the field ids are per-source.
  // Two inputs sharing an id is a label that points at the wrong box.
  const idBase = `rate-${source}`;
  const busy = phase === "sending";
  const canSubmit = !busy && EMAIL_PATTERN.test(email.trim());

  async function submit() {
    if (!canSubmit) return;
    setPhase("sending");
    setError(null);

    /* 🔴 BUILT OUTSIDE THE try, AND OPTIONAL-CHAINED. Both halves are the Sep 2
       2026 fix for a gate that failed for every user.

       `report` used to be read unguarded, and RoomGate rendered this component
       without it - so this threw on undefined, INSIDE the try, and the catch
       below reported a connection error for a request that had never been
       built. The lie was worse than the crash: it sent readers to check their
       wifi, and it looked like an outage rather than a bug.

       Optional chaining degrades a missing report to an UNSEGMENTED signup -
       the address still lands, the format columns are null - which is strictly
       better than losing the address. It is a backstop, not the fix: callers
       pass a real report, and a null column here means somebody forgot one.

       Building it out here also narrows the catch to what it claims to cover.
       Nothing but fetch and the response parse runs inside it now, so
       "could not reach the server" can only ever mean exactly that. */
    const body = JSON.stringify({
      email: email.trim(),
      draftAt: date ? epochFromLocalDate(date) : null,
      teams: report?.teams ?? null,
      budget: report?.budget ?? null,
      isSflex: report?.isSflex ?? null,
      qbSlots: report?.qbSlots ?? null,
      positions: report?.positions ?? null,
      source,
    });

    try {
      const resp = await fetch("/api/email/trends-signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.ok) {
        setError(data.error || "Something went wrong. Try again in a moment.");
        setPhase("error");
        return;
      }
      setPhase("done");
      if (onSubmitted) onSubmitted();
    } catch {
      setError("Could not reach the server. Check your connection and try again.");
      setPhase("error");
    }
  }

  if (phase === "done") {
    return (
      <div style={card} data-testid={testId}>
        <div style={successText}>{doneText}</div>
      </div>
    );
  }

  return (
    <div style={card} data-testid={testId}>
      <div style={{ fontSize: SZ.sub, fontWeight: 800, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: SZ.small, color: C.text.secondary, lineHeight: 1.5, marginBottom: 12 }}>
        {blurb}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <div style={{ flex: "1 1 220px" }}>
          <label htmlFor={`${idBase}-email`} style={label}>
            Email
          </label>
          <input
            id={`${idBase}-email`}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            autoComplete="email"
            placeholder="you@example.com"
            style={input}
          />
        </div>
        {/* Absent, not disabled: a greyed-out field the form does not want is
            still a field the reader has to decide about. With no date entered
            the post carries draftAt null, exactly as leaving it blank always
            did. */}
        {askDate && (
          <div style={{ flex: "1 1 150px" }}>
            <label htmlFor={`${idBase}-date`} style={label}>
              Next draft date (optional)
            </label>
            <input
              id={`${idBase}-date`}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              disabled={busy}
              style={input}
            />
          </div>
        )}
      </div>
      <div style={{ marginTop: 10 }}>
        <button onClick={submit} disabled={!canSubmit} style={primaryBtn(canSubmit)}>
          {busy ? "Sending…" : cta}
        </button>
      </div>
      {phase === "error" && <div style={errorText}>{error}</div>}
      <div style={fineText}>No spam, unsubscribe any time.</div>
    </div>
  );
}
